import { usePage } from '@inertiajs/react'
import type { ReactNode } from 'react'
import AppShell, { type ShellNavGroup } from '../design-system/components/AppShell'
import { useConversationsLive } from '../lib/useConversationsLive'
import { useI18n } from '../lib/useI18n'
import AnnouncementBanners, { type Announcement } from '../pages/portal/shell/AnnouncementBanners'
import CompanySwitcher from '../pages/portal/shell/CompanySwitcher'
import ImpersonationBanner from '../pages/portal/shell/ImpersonationBanner'

type Impersonation = { operator_email: string; reason: string; expires_at: string }

function buildNav(conversationsUnread: number, t: (key: string) => string): ShellNavGroup[] {
  return [
    {
      items: [
        { href: '/', label: t('app.nav.dashboard'), icon: 'layout-dashboard' },
        { href: '/shipments', label: t('app.nav.shipments'), icon: 'package' },
        {
          href: '/conversations',
          label: t('portal.nav.conversations'),
          icon: 'messages-square',
          badge: conversationsUnread,
          feature: 'module.conversations',
        },
        { href: '/quotes', label: t('portal.nav.quotes'), icon: 'file-text' },
        {
          href: '/invoices',
          label: t('portal.nav.invoices'),
          icon: 'receipt',
          feature: 'module.invoices',
        },
      ],
    },
    {
      // Personal account + the company-wide settings hub (one button each, the
      // hub fans out to team / developers / notification channels via cards).
      section: t('portal.section.account'),
      items: [
        { href: '/account', label: t('portal.nav.account'), icon: 'user' },
        { href: '/settings', label: t('portal.nav.settings'), icon: 'settings' },
      ],
    },
  ]
}

/**
 * Customer portal shell (docs/architecture.md §7.2) — the FreightOS AppShell
 * configured with the portal nav, the active-company switcher under the brand,
 * and the operator → portal banners (announcements + support impersonation).
 * Customer-scoped: never renders operator-only navigation.
 */
export default function PortalShell({ children }: { children: ReactNode }) {
  const { props } = usePage()
  const { t } = useI18n()
  const impersonation = props.impersonation as Impersonation | null
  const announcements = (props.announcements as Announcement[] | undefined) ?? []
  const conversationsUnread = (props.conversations_unread as number | undefined) ?? 0
  const companyId = (props.current_company as { id?: string } | null)?.id ?? null
  // Live badge + inbox for the active company's conversations (no polling).
  useConversationsLive({ customerId: companyId, enabled: !!companyId })

  return (
    <AppShell
      subtitle={t('portal.shell.subtitle')}
      nav={buildNav(conversationsUnread, t)}
      hideCrumb
      searchAlign="center"
      search={{ href: '/search', placeholder: t('portal.shell.search') }}
      bottomNav={[
        { href: '/', label: t('app.nav.dashboard'), icon: 'layout-dashboard' },
        { href: '/shipments', label: t('app.nav.shipments'), icon: 'package' },
        {
          href: '/conversations',
          label: t('portal.nav.conversations'),
          icon: 'messages-square',
          badge: conversationsUnread,
          feature: 'module.conversations',
        },
        { href: '/quotes', label: t('portal.nav.quotes'), icon: 'file-text' },
        {
          href: '/invoices',
          label: t('portal.nav.invoices'),
          icon: 'receipt',
          feature: 'module.invoices',
        },
      ]}
      sidebarTop={<CompanySwitcher />}
      banners={
        <>
          {impersonation && <ImpersonationBanner {...impersonation} />}
          <AnnouncementBanners announcements={announcements} />
        </>
      }
    >
      {children}
    </AppShell>
  )
}
